export type PortalSurface = 'workspace' | 'vendor' | 'admin';

export interface PortalSwitchIntent {
  target: PortalSurface;
  startedAt: number;
}

const PORTAL_SWITCH_STORAGE_KEY = 'portal-switch-intent';
const PORTAL_SWITCH_EVENT = 'supplyspark:portal-switch-intent';
const PORTAL_SWITCH_MAX_AGE_MS = 10_000;

function canUseBrowserStorage() {
  try {
    return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
  } catch {
    return false;
  }
}

function removeStoredPortalSwitchIntent() {
  if (!canUseBrowserStorage()) {
    return;
  }

  try {
    window.sessionStorage.removeItem(PORTAL_SWITCH_STORAGE_KEY);
  } catch {
    // Storage can be blocked in privacy-restricted browser modes.
  }
}

function emitPortalSwitchIntentChange(intent: PortalSwitchIntent | null) {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<PortalSwitchIntent | null>(PORTAL_SWITCH_EVENT, {
      detail: intent,
    }),
  );
}

export function readPortalSwitchIntent() {
  if (!canUseBrowserStorage()) {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(PORTAL_SWITCH_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<PortalSwitchIntent> | null;
    if (
      !parsed ||
      (parsed.target !== 'workspace' && parsed.target !== 'vendor' && parsed.target !== 'admin') ||
      typeof parsed.startedAt !== 'number'
    ) {
      removeStoredPortalSwitchIntent();
      return null;
    }

    if (Date.now() - parsed.startedAt > PORTAL_SWITCH_MAX_AGE_MS) {
      removeStoredPortalSwitchIntent();
      return null;
    }

    return parsed as PortalSwitchIntent;
  } catch {
    removeStoredPortalSwitchIntent();
    return null;
  }
}

export function beginPortalSwitch(target: PortalSurface) {
  if (!canUseBrowserStorage()) {
    return;
  }

  const intent = {
    target,
    startedAt: Date.now(),
  } satisfies PortalSwitchIntent;

  try {
    window.sessionStorage.setItem(PORTAL_SWITCH_STORAGE_KEY, JSON.stringify(intent));
  } catch {
    return;
  }

  emitPortalSwitchIntentChange(intent);
}

export function clearPortalSwitchIntent(target?: PortalSurface) {
  const currentIntent = readPortalSwitchIntent();

  if (!canUseBrowserStorage()) {
    return;
  }

  if (!currentIntent) {
    emitPortalSwitchIntentChange(null);
    return;
  }

  if (target && currentIntent.target !== target) {
    return;
  }

  removeStoredPortalSwitchIntent();
  emitPortalSwitchIntentChange(null);
}

export function subscribeToPortalSwitchIntent(
  listener: (intent: PortalSwitchIntent | null) => void,
) {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  let expiryTimeout: ReturnType<typeof window.setTimeout> | null = null;

  const clearExpiryTimeout = () => {
    if (expiryTimeout !== null) {
      window.clearTimeout(expiryTimeout);
      expiryTimeout = null;
    }
  };

  const syncListener = (intent: PortalSwitchIntent | null) => {
    clearExpiryTimeout();

    if (intent) {
      const remainingMs = PORTAL_SWITCH_MAX_AGE_MS - (Date.now() - intent.startedAt);

      if (remainingMs > 0) {
        expiryTimeout = window.setTimeout(() => {
          listener(readPortalSwitchIntent());
        }, remainingMs + 1);
      }
    }

    listener(intent);
  };

  const handlePortalSwitchIntentChange = (event: Event) => {
    const nextIntent = readPortalSwitchIntent();

    if (
      event instanceof CustomEvent &&
      event.detail &&
      nextIntent === null
    ) {
      syncListener(null);
      return;
    }

    syncListener(nextIntent ?? null);
  };

  window.addEventListener(PORTAL_SWITCH_EVENT, handlePortalSwitchIntentChange);
  syncListener(readPortalSwitchIntent());

  return () => {
    clearExpiryTimeout();
    window.removeEventListener(PORTAL_SWITCH_EVENT, handlePortalSwitchIntentChange);
  };
}
